import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { PorangeButton } from "@/components/atoms/PorangeButton.jsx";
import { SocialProofAvatars } from '@/components/molecules/SocialProofAvatars.jsx';
import { motion } from 'framer-motion';
import Image from 'next/image';

export const Statement = ({ onAnswer, previousAnswer }) => {
    const [isExiting, setIsExiting] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const [count, setCount] = useState(0);
    const [showFaces, setShowFaces] = useState(false);
    
    useEffect(() => {
        // Animate count up to 28.1k
        const targetCount = 28.1;
        const duration = 2000;
        const steps = 50;
        const increment = duration / steps;
        
        let current = 0;
        const timer = setInterval(() => {
            if (current < steps) {
                current += 1;
                setCount((current / steps) * targetCount);
            } else {
                clearInterval(timer);
                setShowFaces(true); // Show faces after count completes
            }
        }, increment);

        setTimeout(() => setShowButton(true), 1500);
        return () => clearInterval(timer);
    }, []);

    const handleContinue = () => {
        setShowButton(false);
        setTimeout(() => {
            setIsExiting(true);
            setTimeout(() => onAnswer('continue'), 800);
        }, 400);
    };

    const generateAvatar = (usedIndices = []) => {
        const genders = ['men', 'women'];
        const maxPerGender = 70; // Maximum available indices per gender
        let gender, photoIndex, avatarUrl;
        
        do {
            gender = genders[Math.floor(Math.random() * genders.length)];
            photoIndex = Math.floor(Math.random() * maxPerGender) + 1;
            avatarUrl = `https://randomuser.me/api/portraits/${gender}/${photoIndex}.jpg`;
        } while (usedIndices.includes(avatarUrl));
        
        return avatarUrl;
    };

    const CommunityScroll = () => {
        const [gridItems, setGridItems] = useState([]);

        useEffect(() => {
            const usedUrls = new Set();
            const totalAvatars = 70;
            const newAvatars = [];

            while (newAvatars.length < totalAvatars) {
                const avatarUrl = generateAvatar(Array.from(usedUrls));
                if (!usedUrls.has(avatarUrl)) {
                    usedUrls.add(avatarUrl);
                    newAvatars.push(avatarUrl);
                }
            }

            setGridItems(newAvatars);
        }, []);

        return (
            <div className="overflow-hidden w-screen -mx-6 mt-8">
                <motion.div
                    className="grid grid-rows-5 grid-flow-col gap-4 px-6"
                    initial={{ opacity: 0, x: 0 }}
                    animate={{
                        opacity: showFaces ? 1 : 0,
                        x: showFaces ? -1200 : 0,
                    }}
                    transition={{
                        opacity: { duration: 1 },
                        x: {
                            duration: 150,
                            delay: 0.5 // Slight delay after fade in
                        },
                    }}
                >
                    {gridItems.map((url, index) => (
                        <div
                            key={index}
                            className="w-[85px] h-[85px] relative"
                        >
                            <img 
                                src={url}
                                alt="Community member"
                                className="w-full h-full rounded-full ring-2 ring-white object-cover"
                                loading="lazy"
                            />
                        </div>
                    ))}
                </motion.div>
            </div>
        );
    };

    return (
        <div className={classNames(
            "relative h-full flex flex-col items-center justify-start",
            "transition-all duration-500 ease-in-out",
            isExiting ? "opacity-0 transform translate-y-10" : "opacity-100 transform translate-y-0"
        )}>
            <div className="text-center space-y-4 max-w-[600px] mt-[48px] px-6">
                {/* Counter Section */}
                <div className={classNames(
                    "transition-all duration-500",
                    { "opacity-0 translate-y-4": !showButton, "opacity-100 translate-y-0": showButton }
                )}>
                    <div className="text-[28px] md:text-[40px] leading-tight font-bold text-black mb-2">
                        We'll connect you with
                    </div>
                    <div className="text-7xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-orange-500 to-pink-500">
                        {count.toFixed(1)}k
                    </div>
                    <div className="text-[28px] md:text-[40px] leading-tight font-bold text-black">
                        gluten-free parents, kids, and friends
                    </div>
                </div>
            </div>

            {/* Scrolling Faces Container */}
            <div className="flex-1 flex items-center justify-center w-full overflow-hidden">
                <CommunityScroll />
            </div>

            {/* Continue Button with stronger gradient */}
            <div className={classNames(
                "fixed bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-transparent",
                "transition-all duration-500 pt-32 pb-6 px-5", // Increased padding-top for stronger gradient
                showButton ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 pointer-events-none"
            )}>
                <PorangeButton
                    full={true}
                    className="h-[56px] rounded-[16px] text-lg shadow-md"
                    onClick={handleContinue}
                >
                    Continue
                </PorangeButton>
            </div>
        </div>
    );
};

export default Statement;
